import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AppProvider, useApp } from './context/AppContext';
import ScrollToTop from './components/ScrollToTop';
import './App.css';

// Guest Pages
import HomePage from './pages/HomePage';
import RoomsPage from './pages/RoomsPage';
import RoomDetailPage from './pages/RoomDetailPage';
import BookingPage from './pages/BookingPage';
import PaymentPage from './pages/PaymentPage';
import ConfirmationPage from './pages/ConfirmationPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import CookiePolicy from './pages/CookiePolicy';
import BookingLookupPage from './pages/BookingLookupPage';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminRooms from './pages/admin/AdminRooms';
import AdminPricing from './pages/admin/AdminPricing';
import AdminReservations from './pages/admin/AdminReservations';
import AdminCalendar from './pages/admin/AdminCalendar';
import AdminReports from './pages/admin/AdminReports';
import AdminChannels from './pages/admin/AdminChannels';
import AdminVATSettings from './pages/admin/AdminVATSettings';
import AdminAllotments from './pages/admin/AdminAllotments';
import AdminDiscounts from './pages/admin/AdminDiscounts';
import AdminLayout from './components/AdminLayout';

const ProtectedRoute = ({ children }) => {
  const { adminToken } = useApp();
  if (!adminToken) return <Navigate to="/admin/login" replace />;
  return children;
};

function AppRoutes() {
  const location = useLocation();
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', { page_path: location.pathname + location.search });
    }
  }, [location]);
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/rooms" element={<RoomsPage />} />
      <Route path="/rooms/:id" element={<RoomDetailPage />} />
      <Route path="/booking" element={<BookingPage />} />
      <Route path="/payment" element={<PaymentPage />} />
      <Route path="/payment/callback" element={<PaymentPage />} />
      <Route path="/confirmation/:ref" element={<ConfirmationPage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfUse />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/my-booking" element={<BookingLookupPage />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/admin/dashboard" element={<ProtectedRoute><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/rooms" element={<ProtectedRoute><AdminLayout><AdminRooms /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/pricing" element={<ProtectedRoute><AdminLayout><AdminPricing /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/reservations" element={<ProtectedRoute><AdminLayout><AdminReservations /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/calendar" element={<ProtectedRoute><AdminLayout><AdminCalendar /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/reports" element={<ProtectedRoute><AdminLayout><AdminReports /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/channels" element={<ProtectedRoute><AdminLayout><AdminChannels /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/vat-settings" element={<ProtectedRoute><AdminLayout><AdminVATSettings /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/allotments" element={<ProtectedRoute><AdminLayout><AdminAllotments /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/discounts" element={<ProtectedRoute><AdminLayout><AdminDiscounts /></AdminLayout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AppProvider>
      <Router>
        <ScrollToTop />
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </Router>
    </AppProvider>
  );
}

export default App;
